<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, watch, withDefaults } from 'vue'
import BaseButtonMuted from '../BaseButtonMuted.vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/solid'

interface Props {
    modelValue: number
    totalCount: number
    perPage?: number
}
const props = withDefaults(defineProps<Props>(), {
    perPage: 10,
})

const currentPage = ref(props.modelValue)
const pagesCount = computed(() => Math.ceil(props.totalCount / props.perPage))

const emit = defineEmits(['update:modelValue'])

watch(currentPage, (val) => emit('update:modelValue', val))

function nextPage(): void {
    if (currentPage.value < pagesCount.value) {
        currentPage.value++
    }
}
function prevPage(): void {
    if (currentPage.value > 1) {
        currentPage.value--
    }
}

const isFirst = computed(() => currentPage.value === 1)
const isLast = computed(() => currentPage.value === pagesCount.value)

const rangeBottom = computed(() => (currentPage.value - 1) * props.perPage + 1)
const rangeTop = computed(() => {
    const tempTop = rangeBottom.value + props.perPage - 1
    return tempTop <= props.totalCount ? tempTop : props.totalCount
})
</script>

<template>
    <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div class="flex flex-1 justify-between sm:hidden">
            <BaseButtonMuted :disabled="isFirst" @click="prevPage">{{ $t('table.previous') }}</BaseButtonMuted>
            <BaseButtonMuted :disabled="isLast" @click="nextPage">{{ $t('table.next') }}</BaseButtonMuted>
        </div>
        <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
                <p class="text-sm text-gray-700">
                    {{
                        $t('table.pagination_results_overview', {
                            from: rangeBottom,
                            to: rangeTop,
                            total: props.totalCount,
                        })
                    }}
                </p>
            </div>
            <div>
                <nav class="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                        :disabled="isFirst"
                        class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-slate-100"
                        @click="prevPage">
                        <span class="sr-only">{{ $t('table.previous') }}</span>
                        <ChevronLeftIcon class="w-5" />
                    </button>

                    <button
                        v-if="currentPage >= 2"
                        class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
                        @click="currentPage = 1">
                        1
                    </button>

                    <span
                        v-if="currentPage >= 3"
                        class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                        ...
                    </span>
                    <!--                    <button-->
                    <!--                        v-if="currentPage - 1 >= 1"-->
                    <!--                        class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"-->
                    <!--                        @click="currentPage -= 1">-->
                    <!--                        {{ currentPage - 1 }}-->
                    <!--                    </button>-->

                    <!-- Current Page -->
                    <span
                        aria-current="page"
                        class="relative z-10 inline-flex items-center border border-primary bg-primary-50 px-4 py-2 text-sm font-medium text-primary">
                        {{ currentPage }}
                    </span>
                    <!--                    <button-->
                    <!--                        v-if="currentPage + 1 <= pagesCount"-->
                    <!--                        class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"-->
                    <!--                        @click="currentPage += 1">-->
                    <!--                        {{ currentPage + 1 }}-->
                    <!--                    </button>-->
                    <span
                        v-if="currentPage + 2 <= pagesCount"
                        class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                        ...
                    </span>
                    <button
                        v-if="currentPage + 1 <= pagesCount"
                        class="relative inline-flex items-center border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50"
                        @click="currentPage = pagesCount">
                        {{ pagesCount }}
                    </button>
                    <button
                        class="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-slate-100"
                        :disabled="isLast"
                        @click="nextPage">
                        <span class="sr-only">{{ $t('table.next') }}</span>
                        <ChevronRightIcon class="w-5" />
                    </button>
                </nav>
            </div>
        </div>
    </div>
</template>
